import { Injectable, inject } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ROUTER_NAVIGATION,
  ROUTER_REQUEST,
  RouterRequestAction
} from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import {
  CoreRouterActionGoBack,
  CoreRouterActionNavigateBack,
  CoreRouterActionNavigateForward,
  CoreRouterActionNavigateRoot,
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterActionsTypes,
  CoreRouterNavigationIntent
} from './core-router.actions';
import { CoreActionSetPreviousAttemptingUrl } from './core.actions';
import { CorePageModule } from './core.models';
import { selectCorePageModule } from './core.reducer';

@Injectable()
export class CoreRouterEffects {
  private store$ = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private navController = inject(NavController);

  goBack = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreRouterActionsTypes.GO_BACK),
        map((action: CoreRouterActionGoBack) => action.payload),
        tap(payload => this.navController.back(payload))
      ),
    { dispatch: false }
  );

  navigateBack = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreRouterActionsTypes.NAVIGATE_BACK),
        map((action: CoreRouterActionNavigateBack) => action.payload),
        tap(payload =>
          this.navController.navigateBack(payload.url, payload.options)
        )
      ),
    { dispatch: false }
  );

  navigateForward = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreRouterActionsTypes.NAVIGATE_FORWARD),
        map((action: CoreRouterActionNavigateForward) => action.payload),
        tap(payload =>
          this.navController.navigateForward(payload.url, payload.options)
        )
      ),
    { dispatch: false }
  );

  navigateRoot = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoreRouterActionsTypes.NAVIGATE_ROOT),
        map((action: CoreRouterActionNavigateRoot) => action.payload),
        tap(payload =>
          this.navController.navigateRoot(payload.url, payload.options)
        )
      ),
    { dispatch: false }
  );

  navigateToPageModule$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreRouterActionsTypes.NAVIGATE_TO_PAGE_MODULE),
      switchMap((action: CoreRouterActionNavigateToPageModule) =>
        this.store$.pipe(
          selectCorePageModule(action.payload.id),
          map(module => [action, module])
        )
      ),
      switchMap(
        ([action, module]: [
          CoreRouterActionNavigateToPageModule,
          CorePageModule
        ]) => {
          const pageIndex = action.payload.pageIndex || 0;

          if (!module) {
            throw new Error(
              `[CoreRouter] Navigate to page module ${action.payload.id}, but none found !`
            );
          }

          if (!module.routes || module.routes.length < pageIndex) {
            throw new Error(
              `[CoreRouter] Navigate to page module ${action.payload.id}, but no route found !`
            );
          }

          let navigation$: Observable<CoreRouterActions>;

          switch (action.payload.intent) {
            case CoreRouterNavigationIntent.BACK:
              navigation$ = of(
                action.payload.asRoot
                  ? new CoreRouterActionNavigateRoot({
                      url: module.routes[pageIndex].path,
                      options: { animationDirection: 'back' }
                    })
                  : new CoreRouterActionNavigateBack({
                      url: module.routes[pageIndex].path
                    })
              );
              break;

            case CoreRouterNavigationIntent.FORWARD:
              navigation$ = of(
                action.payload.asRoot
                  ? new CoreRouterActionNavigateRoot({
                      url: module.routes[pageIndex].path,
                      options: { animationDirection: 'forward' }
                    })
                  : new CoreRouterActionNavigateForward({
                      url: module.routes[pageIndex].path
                    })
              );
              break;

            default:
              navigation$ = of(
                new CoreRouterActionNavigateRoot({
                  url: module.routes[pageIndex].path
                })
              );
          }

          return navigation$;
        }
      )
    )
  );

  setPreviousAttemptingUrl$: Observable<CoreActionSetPreviousAttemptingUrl> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ROUTER_REQUEST),
        map((action: RouterRequestAction) => action.payload.routerState.url),
        filter(url => !!url),
        switchMap(url =>
          this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            map(() => url)
          )
        ),
        map(url => new CoreActionSetPreviousAttemptingUrl(url))
      )
    );
}
